import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, ExternalLink, Building2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { BRANCHES, mapEmbedUrl } from "@/lib/branches";

export default function LocationsSection() {
  const { t, lang } = useLang();
  const [activeIdx, setActiveIdx] = useState(0);
  const active = t.locations.list[activeIdx];
  
  // Pinned on the exact coordinates from the company's own Maps links.
  const mapSrc = mapEmbedUrl(BRANCHES[activeIdx] || BRANCHES[0], lang);

  return (
    <section data-testid="locations-section" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Decorative gradient */}
      <div aria-hidden className="absolute -top-40 end-0 w-[32rem] h-[32rem] rounded-full bg-green-500/5 blur-3xl pointer-events-none" />

      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="text-[15px] font-semibold text-[var(--accent-ink)]">{t.locations.eyebrow}</p>
          <h2 className="type-display mt-4 text-[clamp(2rem,3.8vw,3.4rem)]">
            {t.locations.title}
          </h2>
          <p className="mt-4 text-base lg:text-lg text-muted-foreground leading-relaxed">
            {t.locations.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Cards list */}
          <div className="lg:col-span-5 space-y-4">
            {t.locations.list.map((loc, i) => {
              const isActive = i === activeIdx;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`location-card-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`w-full text-start rounded-2xl border p-5 lg:p-6 transition-all cursor-pointer ${
                    isActive
                      ? "bg-slate-950 border-green-500 text-white shadow-xl"
                      : "bg-card border-border hover:border-green-500/60 card-lift"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
                      isActive ? "bg-green-500 text-slate-950" : "bg-green-500/10 text-green-600 dark:text-green-400"
                    }`}>
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className={`text-lg font-extrabold ${isActive ? "text-white" : ""}`}>
                          {loc.city}
                        </h3>
                        <span className={`text-xs font-semibold ${
                          isActive ? "text-green-400" : "text-muted-foreground"
                        }`}>
                          {loc.type}
                        </span>
                      </div>
                      <p className={`mt-1 text-sm ${isActive ? "text-slate-300" : "text-muted-foreground"}`}>
                        <MapPin className="inline w-3.5 h-3.5 me-1 align-text-top" />
                        {loc.address}
                      </p>
                      <p className={`mt-1 text-sm font-semibold ${isActive ? "text-green-400" : "text-foreground"}`}>
                        <Phone className="inline w-3.5 h-3.5 me-1 align-text-top" />
                        <bdi dir="ltr">{loc.phone}</bdi>
                      </p>
                      {loc.phone2 && (
                        <p className={`mt-0.5 text-sm font-semibold ${isActive ? "text-green-400" : "text-foreground"}`}>
                          <Phone className="inline w-3.5 h-3.5 me-1 align-text-top" />
                          <bdi dir="ltr">{loc.phone2}</bdi>
                        </p>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        <a
                          href={loc.mapsUrl || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.mapsQuery)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`location-directions-${i}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            className={`h-9 rounded-full font-bold ${
                              isActive ? "btn-amber border-0" : "bg-slate-900 text-white hover:bg-slate-800"
                            }`}
                          >
                            {t.locations.directions}
                            <ExternalLink className="w-3.5 h-3.5 ms-1.5" />
                          </Button>
                        </a>
                        <a
                          href={`tel:${loc.phone.replace(/\s/g, "")}`}
                          data-testid={`location-call-${i}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-9 rounded-full font-bold ${
                              isActive ? "bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" : ""
                            }`}
                          >
                            {t.locations.call}
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7 rounded-3xl overflow-hidden border border-border bg-muted min-h-[420px] lg:min-h-full relative"
            data-testid="location-map"
          >
            <iframe
              key={activeIdx}
              title={`map-${active.city}`}
              src={mapSrc}
              className="w-full h-full min-h-[420px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
